import { Grid, Skeleton } from '@mui/material';
import { EntitlementGroupInterface } from '@thro-types/access-handler';
import { Product } from '@thro-types/leihbar';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSelfEntitlementGroupsQuery } from 'api/hooks/users';
import { canUserEdit } from 'app/containers/LeihBar/helper';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { ProductInfoDisplay } from './components/ProductInfoDisplay';

interface Props {
  product: Product;
  leihBarEntitlementGroups: EntitlementGroupInterface[];
}
export const ProductInfo = ({ product, leihBarEntitlementGroups }: Props) => {
  const { t } = useTranslation();
  const { user, permissions } = useAuthContext();
  const { data: myEntitlementGroups } = useSelfEntitlementGroupsQuery();

  const canEdit = canUserEdit(product, permissions, user, myEntitlementGroups);

  return (
    <Grid item xs={12} md={12} lg={12} className="product-detail__job__content">
      <ProductInfoDisplay label={t(translations.pages.leihbar.product.location())}>
        {product.location?.name}
      </ProductInfoDisplay>
      {product.lenderGroupId && (
        <ProductInfoDisplay label={t(translations.pages.leihbar.product.domain())}>
          {leihBarEntitlementGroups ? (
            leihBarEntitlementGroups.find(eg => eg.id === product.lenderGroupId)?.name
          ) : (
            <Skeleton width={250} />
          )}
        </ProductInfoDisplay>
      )}
      <ProductInfoDisplay label={t(translations.pages.leihbar.product.categories())}>
        {product.categories?.map(c => c.name).join(', ')}
      </ProductInfoDisplay>
      <ProductInfoDisplay label={t(translations.pages.leihbar.product.create.maxBooking())}>
        {`${product.maxBooking?.value} ${
          product.maxBooking
            ? t(translations.pages.leihbar.product.create.units[product.maxBooking.unit]())
            : t(translations.pages.leihbar.product.noMaxBooking())
        }`}
      </ProductInfoDisplay>
      <ProductInfoDisplay label={t(translations.pages.leihbar.product.create.preparationTime())}>
        {`${product.preparationTime?.value} ${
          product.preparationTime
            ? t(translations.pages.leihbar.product.create.units[product.preparationTime.unit]())
            : t(translations.pages.leihbar.product.noPreparationTime())
        }`}
      </ProductInfoDisplay>
      {product.maxPreBooking && (
        <ProductInfoDisplay label={t(translations.pages.leihbar.product.create.maxPreBooking())}>
          {`${product.maxPreBooking} ${t(translations.common.days())}`}
        </ProductInfoDisplay>
      )}
      {product.hint && (
        <ProductInfoDisplay label={t(translations.pages.leihbar.product.hint())}>
          {product.hint}
        </ProductInfoDisplay>
      )}
      <ProductInfoDisplay label={t(translations.pages.leihbar.product.create.lender.email())}>
        <a
          className="product-info-display__link"
          href={`mailto:${product.lender?.email}?subject=${product.name}`}
        >
          {product.lender?.email}
        </a>
      </ProductInfoDisplay>
      {product.lender?.phone && (
        <ProductInfoDisplay label={t(translations.pages.leihbar.product.create.lender.number())}>
          <a href={`tel:${product.lender?.phone}`}>{product.lender?.phone}</a>
        </ProductInfoDisplay>
      )}
      {canEdit && !!product.editors?.length && (
        <ProductInfoDisplay
          label={t(translations.pages.leihbar.product.create.editorGroupId())}
          tooltip={t(translations.pages.leihbar.product.tooltips.editors())}
        >
          {product.editors.map(editor => editor.email).join(', ')}
        </ProductInfoDisplay>
      )}
    </Grid>
  );
};
